import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle, FaRedoAlt } from "react-icons/fa";
import {
  IoPerson,
  IoPersonOutline,
  IoCallOutline,
  IoMailOutline,
  IoLockClosedOutline,
  IoKeypadOutline,
  IoEye,
  IoEyeOff,
} from "react-icons/io5";
import { http } from "../lib/http";
import "./AuthForms.css";
import { useI18n } from '../contexts/I18n';
import { useAuth } from '../contexts/AuthContext';
import { getBookingIntent, clearBookingIntent } from '../utils/bookingIntent';
import { PASSWORD_RE, passwordRequirements } from '../utils/passwordPolicy';

const emailRx = /^[^\s@]+@[^\s@]+$/;
const phoneRx = /^\+?[0-9]{8,15}$/;
const PUB = process.env.PUBLIC_URL || "";
/* Local file — S3 welcome URL currently returns 403 in browser */
const WELCOME_VIDEO_SRC = `${PUB}/videos/welcome.mp4`;
const HERO_BRAND_LOGO = `${PUB}/images/ehgezly-hero-logo.png?v=4`;

export default function Register() {
  const { t, lang } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const { state } = useLocation();
  const bookingIntent = state?.bookingDetails;
  const hasBookingIntent = state?.bookingIntent || false;
  const isRtl = lang === "ar";

  const [step, setStep] = useState("signup"); // signup | verify | signin
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "", code: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [msg, setMsg] = useState({ err: "", ok: "", busy: false });
  const videoRef = useRef(null);

  const inflight = useRef(null);

  useEffect(() => () => { inflight.current?.abort?.(); }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      el.pause();
      return undefined;
    }
    el.muted = true;
    const play = () => {
      const p = el.play();
      if (p?.catch) p.catch(() => {});
    };
    play();
    el.addEventListener("loadeddata", play);
    return () => el.removeEventListener("loadeddata", play);
  }, []);

  const validName = form.name.trim().length > 1;
  const validPhone = phoneRx.test(form.phone.replace(/\s+/g, ""));
  const validEmail = emailRx.test(form.email);
  const validPass = PASSWORD_RE.test(form.password);

  const readySignup = validName && validPhone && validEmail && validPass && !msg.busy;
  const readySignin = validEmail && !!form.password && !msg.busy;
  const readyVerify = /^\d{4}$/.test(form.code) && !msg.busy;

  const change = (e) => {
    let { name, value } = e.target;
    if (name === "phone") value = value.replace(/[^\d+]/g, "");
    setForm((f) => ({ ...f, [name]: value }));
    if (msg.err || msg.ok) setMsg({ err: "", ok: "", busy: false });
  };

  const afterAuth = async () => {
    if (bookingIntent?.provider) {
      try {
        inflight.current = new AbortController();
        await http.post("/api/bookings", {
          provider: bookingIntent.provider,
          shopName: bookingIntent.shopName,
          selectedService: bookingIntent.selectedService,
          selectedDate: bookingIntent.selectedDay,
          selectedHour: bookingIntent.selectedSlot,
          totalPrice: bookingIntent.totalPrice,
        });
      } catch { /* non-blocking */ }
    }
    nav("/dashboard", { replace: true });
  };

  const withBusy = async (fn) => {
    setMsg({ err: "", ok: "", busy: true });
    try { await fn(); } finally { setMsg((m) => ({ ...m, busy: false })); }
  };

  const signup = (e) => withBusy(async () => {
    e?.preventDefault();
    inflight.current?.abort?.();
    inflight.current = new AbortController();

    const cleanPhone = form.phone.replace(/\s+/g, "");
    try {
      const j = await http.post("/register", {
        name: form.name.trim(),
        phone: cleanPhone,
        email: form.email.toLowerCase().trim(),
        password: form.password,
      });
      setStep("verify");
      if (j?.verificationEmailSent) {
        setMsg({
          err: "",
          ok: `Verification email sent to ${form.email.toLowerCase().trim()}. Please check your inbox and spam folder for the 4-digit code.`,
          busy: false,
        });
      } else {
        setMsg({
          err: "",
          ok:
            j?.message ||
            "Account created. We couldn't send the email — tap Resend below, check spam, or sign in from the login page.",
          busy: false,
        });
      }
    } catch (err) {
      const duplicate = err.status === 409 || /already in use/i.test(err.message || '');
      setMsg({
        err: duplicate
          ? `${err.message} ${t("alreadyHaveAccount")} ${t("signInHere")}.`
          : err.message,
        ok: "",
        busy: false,
      });
    }
  });

  const resend = () => withBusy(async () => {
    if (!validEmail) {
      return setMsg({ err: t("invalidEmail") || "Enter your email.", ok: "", busy: false });
    }
    const email = form.email.toLowerCase().trim();
    try {
      const j = await http.post("/resend-verification-email", { email });
      if (j?.verificationEmailSent) {
        setStep("verify");
        setMsg({
          err: "",
          ok: `Verification email sent to ${email}. Please check your inbox for the 4-digit code.`,
          busy: false,
        });
      } else {
        setMsg({ err: j?.message || "Could not send email. Try again shortly.", ok: "", busy: false });
      }
    } catch (err) {
      setMsg({ err: err.message, ok: "", busy: false });
    }
  });

  const verify = () => withBusy(async () => {
    if (!/^\d{4}$/.test(form.code))
      return setMsg({ err: t('enter 4 Digits') || 'Please enter the 4-digit code', ok: "", busy: false });

    inflight.current?.abort?.();
    inflight.current = new AbortController();
    try {
      await http.post("/verify-email", {
        email: form.email.toLowerCase().trim(),
        verificationCode: form.code,
      });
      setMsg({ err: "", ok: t('emailVerified') || 'Email verified successfully!', busy: false });
      setStep("signin");
    } catch (err) {
      setMsg({ err: err.message, ok: "", busy: false });
    }
  });

  const signin = (e) => withBusy(async () => {
    e?.preventDefault();
    inflight.current?.abort?.();
    inflight.current = new AbortController();
    try {
      const result = await login(form.email.toLowerCase().trim(), form.password);

      if (result.success) {
        const intent = getBookingIntent();
        if (intent && hasBookingIntent) {
          clearBookingIntent();
          nav('/dashboard', {
            state: {
              bookingIntent: intent,
              fromBooking: true
            }
          });
        } else if (result.emailVerified === false) {
          nav('/dashboard', { replace: true, state: { openProfile: true } });
        } else {
          await afterAuth();
        }
      } else {
        setMsg({ err: result.error, ok: "", busy: false });
      }
    } catch (err) {
      setMsg({ err: err.message, ok: "", busy: false });
    }
  });

  const req = useMemo(() => passwordRequirements(form.password), [form.password]);

  const strengthCount =
    (req.len ? 1 : 0) + (req.upper ? 1 : 0) + (req.num ? 1 : 0) + (req.special ? 1 : 0);
  const strengthClass = `strength strength-${Math.min(strengthCount, 4)}`;

  const formTitle =
    step === "verify"
      ? (t("Verify Email") || "Verify Your Email")
      : step === "signin"
        ? t("welcomeBack")
        : t("createAccount");

  const formSubtitle =
    step === "verify"
      ? (t("Sent 4 Digit Code") || `We've sent a 4-digit code to ${form.email.toLowerCase().trim()}.`)
      : step === "signin"
        ? (t("emailVerified") || "Email verified — sign in to continue")
        : t("joinUs");

  return (
    <div
      className={`auth-page auth-page--split auth-page--register${isRtl ? " rtl" : ""}`}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <aside
        className="auth-brand auth-brand--showcase"
        aria-label={t("createAccount")}
      >
        <div className="auth-brand__video" aria-hidden="true">
          <video
            ref={videoRef}
            className="auth-brand__video-el"
            src={WELCOME_VIDEO_SRC}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
          <div className="auth-brand__video-veil" />
        </div>
        <div className="auth-brand__hero-mark">
          <div className="auth-brand__hero-logo-wrap">
            <img
              src={HERO_BRAND_LOGO}
              alt=""
              className="auth-brand__hero-logo"
              draggable="false"
            />
          </div>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-form-panel">
          <header className="auth-form-head">
            <div className="auth-form-head__mark" aria-hidden="true">
              <span className="auth-avatar">
                <IoPerson className="auth-avatar__icon" />
              </span>
            </div>
            <h2>{formTitle}</h2>
            <p>{formSubtitle}</p>
          </header>

          {(msg.err || msg.ok) && (
            <div
              className={msg.err ? "error-message" : "success-message"}
              role="alert"
              aria-live="assertive"
            >
              {msg.err || msg.ok}
            </div>
          )}

          <form
            className="auth-form"
            aria-busy={msg.busy}
            noValidate
            onSubmit={(e) => {
              if (step === "signup") return signup(e);
              if (step === "verify") { e.preventDefault(); return verify(); }
              return signin(e);
            }}
          >
            {step === "signup" && (
              <>
                <div className={`auth-signup-grid${form.password ? " is-password-active" : ""}`}>
                  <div className="auth-signup-fields">
                    <div className="form-group">
                      <label className="form-label form-label-with-icon" htmlFor="reg-name">
                        <span className="form-label-icon" aria-hidden="true">
                          <IoPersonOutline className="form-label-icon-svg" />
                        </span>
                        <span className="form-label-text">{t("fullName")}</span>
                      </label>
                      <input
                        id="reg-name"
                        name="name"
                        placeholder={t("enterYourFullName")}
                        value={form.name}
                        onChange={change}
                        autoComplete="name"
                        className={`form-input ${form.name && !validName ? "invalid" : ""}`}
                        aria-invalid={!!form.name && !validName}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label form-label-with-icon" htmlFor="reg-phone">
                        <span className="form-label-icon" aria-hidden="true">
                          <IoCallOutline className="form-label-icon-svg" />
                        </span>
                        <span className="form-label-text">{t("phoneNumber")}</span>
                      </label>
                      <input
                        id="reg-phone"
                        name="phone"
                        placeholder="+20XXXXXXXXX"
                        value={form.phone}
                        onChange={change}
                        inputMode="tel"
                        autoComplete="tel"
                        dir="ltr"
                        className={`form-input ${form.phone && !validPhone ? "invalid" : ""}`}
                        aria-invalid={!!form.phone && !validPhone}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label form-label-with-icon" htmlFor="reg-email">
                        <span className="form-label-icon" aria-hidden="true">
                          <IoMailOutline className="form-label-icon-svg" />
                        </span>
                        <span className="form-label-text">{t("emailAddress")}</span>
                      </label>
                      <input
                        id="reg-email"
                        name="email"
                        type="email"
                        placeholder={t("enterYourEmail")}
                        value={form.email}
                        onChange={change}
                        autoComplete="email"
                        className={`form-input ${form.email && !validEmail ? "invalid" : ""}`}
                        aria-invalid={!!form.email && !validEmail}
                        required
                      />
                    </div>

                    <div className="form-group form-group--password">
                      <label className="form-label form-label-with-icon" htmlFor="reg-password">
                        <span className="form-label-icon" aria-hidden="true">
                          <IoLockClosedOutline className="form-label-icon-svg" />
                        </span>
                        <span className="form-label-text">{t("password")}</span>
                      </label>
                      <div className="auth-password-field">
                        <input
                          id="reg-password"
                          name="password"
                          type={showPwd ? "text" : "password"}
                          placeholder={t("createStrongPassword")}
                          value={form.password}
                          onChange={change}
                          autoComplete="new-password"
                          className={`form-input ${form.password && !validPass ? "invalid" : ""}`}
                          aria-describedby={form.password ? "pwd-strength pwd-req" : undefined}
                          aria-invalid={!!form.password && !validPass}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPwd((p) => !p)}
                          aria-pressed={showPwd}
                          aria-label={showPwd ? t("hidePassword") : t("showPassword")}
                        >
                          {showPwd ? <IoEye /> : <IoEyeOff />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {form.password ? (
                    <div className="auth-signup-side">
                      <div className="auth-signup-side__inner">
                        <ul id="pwd-req" className="password-requirements auth-signup-reqs" aria-live="polite">
                          <li className={`req-item ${req.len ? "valid" : ""}`}>
                            {req.len ? <FaCheckCircle className="req-icon" /> : <FaTimesCircle className="req-icon" />}
                            <span>{t("reqAtLeast8") || "At least 8 characters"}</span>
                          </li>
                          <li className={`req-item ${req.lower ? "valid" : ""}`}>
                            {req.lower ? <FaCheckCircle className="req-icon" /> : <FaTimesCircle className="req-icon" />}
                            <span>{t("reqLowercase") || "Lowercase letter"}</span>
                          </li>
                          <li className={`req-item ${req.upper ? "valid" : ""}`}>
                            {req.upper ? <FaCheckCircle className="req-icon" /> : <FaTimesCircle className="req-icon" />}
                            <span>{t("reqUppercase") || "Uppercase letter"}</span>
                          </li>
                          <li className={`req-item ${req.num ? "valid" : ""}`}>
                            {req.num ? <FaCheckCircle className="req-icon" /> : <FaTimesCircle className="req-icon" />}
                            <span>{t("reqNumber") || "Number"}</span>
                          </li>
                          <li className={`req-item ${req.special ? "valid" : ""}`}>
                            {req.special ? <FaCheckCircle className="req-icon" /> : <FaTimesCircle className="req-icon" />}
                            <span>{t("reqSpecial") || "Special character (e.g. ! @ # _ . ? -)"}</span>
                          </li>
                        </ul>
                        <div id="pwd-strength" className={`password-strength ${strengthClass}`} aria-hidden>
                          <div className="strength-bar"></div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className={`auth-signup-actions${form.password ? " is-password-active" : ""}`}>
                  <button type="submit" className="auth-button" disabled={!readySignup}>
                    {msg.busy ? <span className="loading-spinner"></span> : t("sendVerification")}
                  </button>
                </div>
              </>
            )}

            {step === "verify" && (
              <>
                <div className="form-group">
                  <label className="form-label form-label-with-icon" htmlFor="reg-code">
                    <span className="form-label-icon" aria-hidden="true">
                      <IoKeypadOutline className="form-label-icon-svg" />
                    </span>
                    <span className="form-label-text">{t("verificationCode") || "Verification Code"}</span>
                  </label>
                  <input
                    id="reg-code"
                    name="code"
                    placeholder={t("enter4DigitCode") || "Enter 4-digit code"}
                    value={form.code}
                    onChange={change}
                    inputMode="numeric"
                    maxLength={4}
                    autoComplete="one-time-code"
                    className="form-input verification-input"
                    aria-label={t("verificationCode") || "Verification Code"}
                    required
                  />
                </div>

                {!form.email && (
                  <div className="form-group">
                    <label className="form-label" htmlFor="reg-verify-email">{t("emailAddress")}</label>
                    <input
                      id="reg-verify-email"
                      name="email"
                      type="email"
                      placeholder={t("enterYourEmail")}
                      value={form.email}
                      onChange={change}
                      className="form-input"
                      required
                    />
                  </div>
                )}

                <div className="verification-actions">
                  <button
                    type="button"
                    className="resend-button"
                    onClick={resend}
                    disabled={msg.busy}
                  >
                    <FaRedoAlt className="resend-icon" />
                    {t("resendCode") || "Resend Code"}
                  </button>

                  <button type="submit" className="auth-button" disabled={!readyVerify}>
                    {msg.busy ? <span className="loading-spinner"></span> : (t("verifyCode") || "Verify Code")}
                  </button>
                </div>
              </>
            )}

            {step === "signin" && (
              <>
                <div className="form-group">
                  <label className="form-label form-label-with-icon" htmlFor="reg-signin-email">
                    <span className="form-label-icon" aria-hidden="true">
                      <IoMailOutline className="form-label-icon-svg" />
                    </span>
                    <span className="form-label-text">{t("emailAddress")}</span>
                  </label>
                  <input
                    id="reg-signin-email"
                    name="email"
                    type="email"
                    placeholder={t("enterYourEmail")}
                    value={form.email}
                    onChange={change}
                    autoComplete="email"
                    className={`form-input ${form.email && !validEmail ? "invalid" : ""}`}
                    aria-invalid={!!form.email && !validEmail}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label form-label-with-icon" htmlFor="reg-signin-password">
                    <span className="form-label-icon" aria-hidden="true">
                      <IoLockClosedOutline className="form-label-icon-svg" />
                    </span>
                    <span className="form-label-text">{t("password")}</span>
                  </label>
                  <div className="auth-password-field">
                    <input
                      id="reg-signin-password"
                      name="password"
                      type={showPwd ? "text" : "password"}
                      placeholder={t("passwordPlaceholder")}
                      value={form.password}
                      onChange={change}
                      autoComplete="current-password"
                      className="form-input"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPwd((p) => !p)}
                      aria-pressed={showPwd}
                      aria-label={showPwd ? t("hidePassword") : t("showPassword")}
                    >
                      {showPwd ? <IoEye /> : <IoEyeOff />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="auth-button" disabled={!readySignin}>
                  {msg.busy ? <span className="loading-spinner"></span> : t("signIn")}
                </button>
              </>
            )}
          </form>

          <div className="auth-footer">
            <p>
              {t("alreadyHaveAccount")}{" "}
              <NavLink to="/login" className="auth-link">
                {t("signInHere")}
              </NavLink>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
